import { Navigate, Link } from "react-router-dom";
import {
    Phone, MapPin, ArrowRight, CheckCircle2, Star, Award, Users, Clock,
    Video, GraduationCap, ShieldCheck, MessageCircle,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import RegistrationForm from "../components/RegistrationForm";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { getOfflineLocalityData, OFFLINE_LOCALITIES } from "../data/offlineLocalityData";
import { SOCIAL } from "../data/siteData";

export default function HomeTuitionLocality({ localitySlug, citySlug }) {
    const data = getOfflineLocalityData(localitySlug, citySlug);
    if (!data) return <Navigate to="/" replace />;

    const { city, locality } = data;

    // Related localities in the same city (for internal linking)
    const otherLocalities = (OFFLINE_LOCALITIES[citySlug] || []).filter((l) => l.slug !== localitySlug);

    // LocalBusiness + EducationalOrganization schema — the ONLY places where
    // "Home Tuition" is a legally valid business offering.
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "EducationalOrganization"],
        "@id": `${data.canonical}#localbusiness`,
        "name": `Anand Tutorials — Home Tuition in ${locality.name}, ${city.name}`,
        "alternateName": [
            `Home Tuition in ${locality.name}`,
            `Home Tutor in ${locality.name}, ${city.name}`,
            `1:1 Home Tuition ${locality.name}`,
            `Anand Tutorials ${locality.name}`,
        ],
        "url": data.canonical,
        "telephone": city.phone,
        "email": "anandhometutorials@gmail.com",
        "image": "https://anandtutorials.com/logo-anand-tutorials.png",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "branchOf": {
            "@type": "EducationalOrganization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
        },
        "knowsAbout": [
            `Home Tuition in ${locality.name}`,
            `Home Tutor in ${locality.name}`,
            `CBSE Home Tutor ${locality.name}`,
            `ICSE Home Tutor ${locality.name}`,
            `NEET Home Coaching ${locality.name}`,
            `JEE Home Coaching ${locality.name}`,
            `Private Home Tutor ${locality.name}`,
        ],
        "areaServed": {
            "@type": "Place",
            "name": `${locality.name}, ${city.name}`,
            "containedInPlace": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "AdministrativeArea", "name": city.state } },
        },
        "address": {
            "@type": "PostalAddress",
            "addressLocality": locality.name,
            "addressRegion": city.state,
            "addressCountry": city.countryCode,
        },
        "geo": { "@type": "GeoCoordinates", "latitude": city.geo.lat, "longitude": city.geo.lng },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "150",
            "bestRating": "5",
            "worstRating": "1",
        },
        "openingHoursSpecification": [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "21:00",
        }],
        "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
    };

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${data.canonical}#service`,
        "serviceType": `Home Tuition & 1:1 Coaching in ${locality.name}, ${city.name}`,
        "provider": { "@id": `${data.canonical}#localbusiness` },
        "areaServed": {
            "@type": "Place",
            "name": `${locality.name}, ${city.name}`,
        },
        "audience": { "@type": "PeopleAudience", "audienceType": "Students Grade 1-12 + NEET / JEE aspirants" },
        "category": "In-home Educational Services",
        "url": data.canonical,
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${data.canonical}#faq`,
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${data.canonical}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Home Tuition", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 3, "name": city.name, "item": `https://anandtutorials.com/${city.slug}` },
            { "@type": "ListItem", "position": 4, "name": locality.name, "item": data.canonical },
        ],
    };

    return (
        <div data-testid={`locality-${localitySlug}-${citySlug}`}>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={data.path}
                canonicalPath={data.path}
                geo={{
                    region: city.regionCode,
                    placename: `${locality.name}, ${city.name}, ${city.state}`,
                    lat: city.geo.lat,
                    lng: city.geo.lng,
                }}
                schemas={[localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema]}
            />

            <Breadcrumbs items={[
                { name: "Home Tuition", to: "/" },
                { name: city.name, to: `/${city.slug}` },
                { name: locality.name },
            ]} />

            <PageHeader
                eyebrow={data.eyebrow}
                title={data.h1}
                subtitle={data.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <a href={`tel:${city.phoneRaw}`} data-testid="loc-cta-call" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        <Phone className="w-4 h-4" /> {city.phone}
                    </a>
                    <Link to="/register" data-testid="loc-cta-demo" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I need a home tutor in ${locality.name}, ${city.name}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#1ebe5a]">
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                </div>
            </PageHeader>

            {/* Trust strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: Users, n: "8,000+", l: `Students taught in ${city.name}` },
                        { i: Star, n: "4.9 / 5", l: `${city.name} parents` },
                        { i: Award, n: "98%", l: "Board pass rate" },
                        { i: Clock, n: "24-48 hrs", l: "Tutor reaches you" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* SEO sections */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                    {data.whySections.map((s, i) => (
                        <article key={i}>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                            <p className="text-slate-700 leading-relaxed text-base sm:text-lg">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* Services offered */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Services in {locality.name}
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">
                        Home Tuition + 1:1 Live Online Classes — same tutor, same plan.
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <MapPin className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">Home Tuition in {locality.name}</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">Verified tutor visits your home in {locality.name}, {city.name}. Punctual, professional, background-verified — with weekly Sunday parent reports.</p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Tutor reaches {locality.name} on schedule</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> All boards & subjects covered</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Free demo before any payment</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 transition-colors">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-4">
                                <Video className="w-6 h-6" />
                            </div>
                            <h3 className="font-heading text-xl font-semibold mb-2">1:1 Live Online Classes</h3>
                            <p className="text-slate-600 leading-relaxed text-sm mb-3">Prefer online? Same tutor, same plan — delivered live on Zoom or Google Meet. Recorded for revision, flexible slots including weekends.</p>
                            <ul className="space-y-1.5 text-sm text-slate-600">
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> One student, one tutor, one screen</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Sessions recorded for revision</li>
                                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> Switch between home &amp; online anytime</li>
                            </ul>
                            <Link to="/online-classes" className="mt-4 inline-flex items-center gap-1 text-[#1E3A8A] font-semibold text-sm hover:gap-2 transition-all">
                                Learn more about online classes <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                        Why {locality.name} parents choose us
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Verified home tutor quality — every session, every visit.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            [GraduationCap, "Verified local tutor pool", `Every ${locality.name} tutor is interviewed, demo-tested and background-verified. Drawn from ${city.localColleges}.`],
                            [Video, "True 1:1 — never batch", "One student, one tutor, one personalised plan. No shared coaching-hall attention."],
                            [ShieldCheck, "Free tutor replacement", "If you're not satisfied, we rematch the tutor at no cost — no questions asked."],
                            [Clock, "Flexible scheduling", "Morning, evening or weekend slots — matched to your child's school + activity schedule."],
                            [Award, "Weekly progress tracking", "Sunday parent reports — what was covered, homework and next week's plan."],
                            [Users, "Dedicated coordinator", "One named coordinator handles all communications — no call-centre runaround."],
                        ].map(([I, t, d]) => (
                            <div key={t} className="p-6 bg-[#F8FAFC] rounded-2xl border border-slate-200">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                    <I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-semibold text-[#0F172A] mb-1">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ + Registration */}
            <section className="py-16 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                            {locality.name} FAQs
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">
                            {locality.name} parents ask — here&apos;s what we tell them
                        </h2>
                        <div className="space-y-3">
                            {data.faqs.map(([q, a], i) => (
                                <details key={q} data-testid={`loc-faq-${i}`} className="group bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 [&_summary::-webkit-details-marker]:hidden">
                                    <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] flex items-center justify-between gap-3">
                                        {q}
                                        <span className="text-[#3B82F6] text-2xl group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-2" id="book-demo">
                        <RegistrationForm
                            title={`Book Free Demo · ${locality.name}, ${city.name}`}
                            defaultCity={city.name}
                        />
                    </div>
                </div>
            </section>

            {/* Other localities in this city */}
            {otherLocalities.length > 0 && (
                <section className="py-12 bg-[#F8FAFC] border-y border-slate-200">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-2">Also Serving</div>
                        <h3 className="font-heading text-xl font-semibold mb-4">Home tuition in other {city.name} localities</h3>
                        <div className="flex flex-wrap justify-center gap-2">
                            {otherLocalities.map((l) => (
                                <Link key={l.slug} to={`/home-tuition-${l.slug}-${city.slug}`} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">
                                    Home tuition in {l.name} →
                                </Link>
                            ))}
                            <Link to={`/${city.slug}`} className="px-4 py-2 bg-[#1E3A8A] text-white border border-[#1E3A8A] rounded-lg text-sm font-semibold hover:bg-[#1E40AF]">
                                All of {city.name} →
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* Final CTA */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Start your child&apos;s success story in {locality.name}.</h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">Free 1:1 demo with a verified {locality.name} home tutor. No payment, no commitment.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href={`tel:${city.phoneRaw}`} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400">
                            <Phone className="w-4 h-4" /> Call {city.phone}
                        </a>
                        <Link to="/register" className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50">
                            Book Free Demo
                        </Link>
                        <a href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I need a home tutor in ${locality.name}, ${city.name}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1ebe5a]">
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
